import { FC, useState } from 'react'

import styled from 'styled-components'

import dayjs from 'src/Helpers/dayjs'
import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'
import { formatDate } from 'src/Constants/formatDate'

import { PrimaryDottedButton } from '../Buttons/PrimaryDottedButton'
import { PrimaryDatePicker } from '../PrimaryDatePicker'

import { TimeSaveButton } from './TimeSaveButton'

import { ScheduleSettingsHeaderProps } from '.'

const TimeContainer = styled.div<{ $isEditable: boolean }>`
  display: flex;
  padding-left: 84px;
  flex-direction: column;
  align-items: flex-start;

  @media screen and (min-width: ${MAX_MOBILE_WIDTH}) {
    padding-left: 0px;
    align-items: flex-end;
    align-self: ${({ $isEditable }) => ($isEditable ? 'stretch' : 'center')};
  }
`
const DateText = styled.div`
  color: ${(props) => props.theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  white-space: nowrap;
  text-align: start;
  @media screen and (min-width: ${MAX_MOBILE_WIDTH}) {
    text-align: end;
  }
`

const StyledPrimaryDottedButton = styled(PrimaryDottedButton)`
  padding: 0;
`

const StyledScheduleDatePicker = styled(PrimaryDatePicker)`
  &.date-picker-dropdown .date-picker-panel-container {
    width: 312px;
    height: 373px;
    display: flex;
    flex-direction: column;
  }

  &.date-picker-dropdown .date-picker-panel-layout {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  &.date-picker-dropdown .date-picker-panels {
    flex: 1;
  }

  &.date-picker-dropdown .date-picker-footer {
    margin-top: auto;
  }

  &.date-picker-dropdown .date-picker-header {
    padding: 16px 16px 16px;
  }

  &.date-picker-dropdown .date-picker-ranges {
    padding: 19px 16px 16px;
    text-align: center;
  }

  &.date-picker-dropdown .date-picker-ranges .date-picker-ok {
    float: none;
    width: 100%;
    margin: 0;
  }

  &.date-picker-dropdown .date-picker-ranges .date-picker-ok button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: transparent;
    box-shadow: none;
    color: ${({ theme }) => theme.color.darkGrey};
    height: auto;
  }

  &.date-picker-dropdown .date-picker-ranges .date-picker-ok button > span {
    text-align: center;
    font-family: 'Segoe UI';
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 150%;
    border-bottom: 1px dashed ${({ theme }) => theme.color.darkGrey};
  }

  &.date-picker-dropdown
    .date-picker-ranges
    .date-picker-ok
    button:hover
    > span,
  &.date-picker-dropdown
    .date-picker-ranges
    .date-picker-ok
    button:focus-visible
    > span {
    border-bottom-color: transparent;
  }

  &.date-picker-dropdown
    .date-picker-ranges
    .date-picker-ok
    button:active
    > span {
    border-bottom-style: solid;
  }

  &.date-picker-dropdown
    .date-picker-ranges
    .date-picker-ok
    button:disabled
    > span {
    color: ${({ theme }) => theme.color.lightGrey};
    border-bottom-color: ${({ theme }) => theme.color.lightGrey};
    border-bottom-style: dashed;
  }

  &.date-picker-dropdown .date-picker-panel,
  &.date-picker-dropdown .date-picker-date-panel {
    width: 100%;
  }

  &.date-picker-dropdown .date-picker-content thead {
    display: none;
  }

  &.date-picker-dropdown .date-picker-body {
    padding-top: 4px;
  }

  &.date-picker-dropdown .date-picker-date-panel .date-picker-body {
    padding: 0 20px 0 16px;
  }

  &.date-picker-dropdown .date-picker-content {
    margin: 0 auto;
  }

  &.date-picker-dropdown .date-picker-content tbody tr td {
    padding: 0;
  }

  &.date-picker-dropdown .date-picker-cell .date-picker-cell-inner {
    min-width: 40px;
    height: 40px;
    line-height: 40px;
    border-radius: 8px;
  }

  &.date-picker-dropdown
    .date-picker-cell:not(.date-picker-cell-selected)
    .date-picker-cell-inner:hover {
    background: ${({ theme }) => theme.color.hoverWhite};
  }

  &.date-picker-dropdown
    .date-picker-cell:not(.date-picker-cell-selected)
    .date-picker-cell-inner:active {
    background: ${({ theme }) => theme.color.lightGrey};
    color: ${({ theme }) => theme.color.mediumGrey};
  }

  &.date-picker-dropdown
    .date-picker-cell-in-view.date-picker-cell-focused:not(
      .date-picker-cell-selected
    )
    .date-picker-cell-inner {
    border: 1px solid ${({ theme }) => theme.color.brandGreen};
    background: ${({ theme }) => theme.color.white};
  }

  &.date-picker-dropdown .date-picker-cell::before {
    border-radius: 8px;
  }

  &.date-picker-dropdown
    .date-picker-cell-in-view.date-picker-cell-selected
    .date-picker-cell-inner:hover {
    box-shadow: ${({ theme }) => theme.shadow.header};
  }

  &.date-picker-dropdown
    .date-picker-cell-in-view.date-picker-cell-selected
    .date-picker-cell-inner:active {
    background: ${({ theme }) => theme.color.darkGreen};
  }

  &.date-picker-dropdown
    .date-picker-cell-in-view.date-picker-cell-selected.date-picker-cell-focused
    .date-picker-cell-inner {
    border: 1px solid ${({ theme }) => theme.color.brandGreen};
    background: ${({ theme }) => theme.color.white};
    color: ${({ theme }) => theme.color.brandGreen};
  }

  &.date-picker-dropdown
    .date-picker-cell-in-view.date-picker-cell-selected.date-picker-cell-disabled
    .date-picker-cell-inner {
    background: ${({ theme }) => theme.color.lightGrey};
    color: ${({ theme }) => theme.unchangingColor.white};
  }

  &.date-picker-dropdown
    .date-picker-cell-in-view.date-picker-cell-today
    .date-picker-cell-inner {
    color: ${({ theme }) => theme.color.darkGrey};
  }

  &.date-picker-dropdown
    .date-picker-cell-in-view.date-picker-cell-today
    .date-picker-cell-inner {
    color: ${({ theme }) => theme.color.mediumGrey};
  }
  &.date-picker-dropdown
    .date-picker-cell-in-view.date-picker-cell-today
    .date-picker-cell-inner::before {
    border: 1px solid ${({ theme }) => theme.color.brandGreen};
    border-radius: 4px;
    inset: 8px;
  }

  &.date-picker-dropdown
    .date-picker-cell-disabled.date-picker-cell-today
    .date-picker-cell-inner::before {
    border: 1px solid ${({ theme }) => theme.color.brandGreen};
    border-radius: 4px;
    inset: 8px;
  }

  &.date-picker-dropdown .date-picker-cell-disabled::before {
    background: transparent;
  }

  &.date-picker-dropdown .date-picker-cell-disabled .date-picker-cell-inner {
    color: ${({ theme }) => theme.color.mediumGrey};
  }

  &.date-picker-dropdown .date-picker-cell:not(.date-picker-cell-in-view) {
    color: ${({ theme }) => theme.color.mediumGrey};
  }
  &.date-picker-dropdown .date-picker-footer {
    border-top: none;
  }
`

const Backdrop = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: ${({ theme }) => `${theme.unchangingColor.black}80`};
  display: ${({ $visible }) => ($visible ? 'block' : 'none')};
  z-index: 999;
`

const Container = styled.div`
  position: relative;
  height: 0;
  z-index: 1000;
`

const MIN_DATE = dayjs().startOf('month')
const MAX_DATE = dayjs().add(1, 'month')
type Props = Pick<
  ScheduleSettingsHeaderProps,
  'startDate' | 'onChangeDate' | 'todayEnable' | 'isEditable'
>

export const ScheduleSettingsHeaderTime: FC<Props> = (props) => {
  const { startDate, todayEnable, onChangeDate, isEditable } = props
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState(
    dayjs.isDayjs(startDate) ? startDate : dayjs(),
  )
  const [prevStartDate, setPrevStartDate] = useState(startDate)

  if (prevStartDate !== startDate) {
    setPrevStartDate(startDate)
    setValue(dayjs.isDayjs(startDate) ? startDate : dayjs())
  }

  const displayedStartDate = dayjs.isDayjs(startDate)
    ? `Start date: ${startDate.format(formatDate.formatDateMonthFull)}`
    : startDate || ''

  const disabledStart = todayEnable ? dayjs().subtract(1, 'day') : dayjs()
  return (
    <TimeContainer $isEditable={Boolean(isEditable)}>
      <Backdrop $visible={open} />
      <Container>
        <StyledScheduleDatePicker
          mode='date'
          needConfirm={true}
          minDate={MIN_DATE}
          maxDate={MAX_DATE}
          getPopupContainer={() => document.body}
          onOpenChange={(isOpen) => setOpen(isOpen)}
          onChange={(currentDate) => setValue(dayjs(currentDate))}
          open={open}
          style={{ visibility: 'hidden', width: 0, height: 0, padding: 0 }}
          disabledDate={(current) =>
            disabledStart >= current || MAX_DATE <= current
          }
          showNow={false}
          hideDisabledOptions
          defaultPickerValue={value}
          value={value}
          onOk={(data) => {
            onChangeDate?.(data)
          }}
          components={{
            button: TimeSaveButton,
          }}
        />
      </Container>
      <DateText>{displayedStartDate}</DateText>
      {isEditable && (
        <StyledPrimaryDottedButton onClick={() => setOpen(true)}>
          Edit
        </StyledPrimaryDottedButton>
      )}
    </TimeContainer>
  )
}
